import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Lock, Eye, CheckCircle, Clock, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import MenuForm from './MenuForm';
import OpcionesManager from './OpcionesManager';
import { publishMenu, closeMenu, deleteMenu } from '../../api/cafeteria';

const ESTADO_STYLES = {
  draft:     { label: 'Borrador',   cls: 'bg-gray-100 text-gray-700' },
  published: { label: 'Publicado',  cls: 'bg-green-100 text-green-700' },
  closed:    { label: 'Cerrado',    cls: 'bg-red-100 text-red-700' }
};

export default function AdminMenus({ menus, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState({});

  const setLoadingFor = (id, val) => setLoading(l => ({ ...l, [id]: val }));

  const handlePublish = async (id) => {
    if (!confirm('¿Publicar este menú? Los empleados podrán hacer sus selecciones.')) return;
    setLoadingFor(id, true);
    try {
      await publishMenu(id);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al publicar');
    } finally { setLoadingFor(id, false); }
  };

  const handleClose = async (id) => {
    if (!confirm('¿Cerrar el menú? Ya no se podrán hacer selecciones.')) return;
    setLoadingFor(id, true);
    try {
      await closeMenu(id);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al cerrar');
    } finally { setLoadingFor(id, false); }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este menú?')) return;
    setLoadingFor(id, true);
    try {
      await deleteMenu(id);
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.error || 'Error al eliminar');
    } finally { setLoadingFor(id, false); }
  };

  const toggleExpanded = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  return (
    <div className="space-y-4">
      {/* Botón crear */}
      {!showForm && (
        <div className="flex justify-end">
          <button
            onClick={() => { setEditing(null); setShowForm(true); }}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
          >
            <Plus className="w-4 h-4" /> Crear Menú
          </button>
        </div>
      )}

      {/* Formulario */}
      {showForm && (
        <MenuForm
          menu={editing}
          onSaved={() => { setShowForm(false); setEditing(null); onRefresh(); }}
          onCancel={() => { setShowForm(false); setEditing(null); }}
        />
      )}

      {/* Lista de menús */}
      {menus.length === 0 && !showForm && (
        <div className="bg-white rounded-2xl shadow p-12 text-center">
          <ChevronDown className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No hay menús creados</p>
          <p className="text-gray-400 text-sm">Crea el primer menú para comenzar</p>
        </div>
      )}

      {menus.map(menu => {
        const est = ESTADO_STYLES[menu.estado] || ESTADO_STYLES.draft;
        const isExpanded = expanded[menu.id];
        const isLoading = loading[menu.id];
        const fechaStr = (menu.fecha || '').split('T')[0];
        const fecha = new Date(fechaStr + 'T00:00:00').toLocaleDateString('es-DO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

        return (
          <div key={menu.id} className="bg-white rounded-2xl shadow overflow-hidden">
            {/* Header del menú */}
            <div className="p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${est.cls}`}>{est.label}</span>
                    <span className="text-gray-500 text-sm flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> Límite: {menu.hora_limite?.slice(0,5)}
                    </span>
                    <span className="text-gray-500 text-sm">
                      {menu.total_opciones || 0} opciones · {menu.total_selecciones || 0} selecciones
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mt-1 capitalize">{fecha}</h3>
                  {menu.descripcion && <p className="text-sm text-gray-500 mt-0.5">{menu.descripcion}</p>}
                </div>

                {/* Acciones */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {menu.estado === 'draft' && (
                    <>
                      <button onClick={() => { setEditing(menu); setShowForm(true); }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="Editar">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => handlePublish(menu.id)} disabled={isLoading}
                        className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-sm font-semibold hover:bg-green-600 disabled:opacity-50">
                        <Eye className="w-3.5 h-3.5 inline mr-1" />Publicar
                      </button>
                      <button onClick={() => handleDelete(menu.id)} disabled={isLoading}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Eliminar">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {menu.estado === 'published' && (
                    <button onClick={() => handleClose(menu.id)} disabled={isLoading}
                      className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 disabled:opacity-50">
                      <Lock className="w-3.5 h-3.5 inline mr-1" />Cerrar
                    </button>
                  )}
                  <button onClick={() => toggleExpanded(menu.id)}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Opciones expandidas */}
            {isExpanded && (
              <div className="border-t border-gray-100 p-5">
                <OpcionesManager menu={menu} onRefresh={onRefresh} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
